import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal, Select, Space, Typography, message } from "antd";
import { LinkOutlined, SwapOutlined } from "@ant-design/icons";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import { fetchDossiers } from "../../../../lib/api/resources/dossiers";
import { useMoveChatConversation } from "../../../../lib/hooks/useChat";

const { Text } = Typography;

const dossierLabel = (dossier: {
  id: string;
  damageNumber?: string | null;
  driver?: {
    fullName?: string | null;
    person?: { fullName?: string | null } | null;
  } | null;
  vehicle?: { licensePlate?: { identifier?: string | null } | null } | null;
}): string => {
  const name =
    dossier.driver?.fullName ??
    dossier.driver?.person?.fullName ??
    "Onbekende bestuurder";
  const plate = dossier.vehicle?.licensePlate?.identifier;
  const ref = plate ?? dossier.damageNumber ?? dossier.id.slice(0, 8);
  return `${name} — ${ref}`;
};

export const ChatMoveDossierButton = ({
  conversationId,
  isLinked,
  block,
}: {
  conversationId: string;
  isLinked: boolean;
  block?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | undefined>(undefined);

  const moveMutation = useMoveChatConversation();

  const dossiersQuery = useQuery<Dossier[]>({
    enabled: open,
    queryKey: ["chat", "dossier-picker", search],
    queryFn: async () => {
      const trimmed = search.trim();
      const base = { itemsPerPage: 10, "exists[archivedAt]": false };

      if (!trimmed) {
        return (await fetchDossiers(base)).member;
      }

      const [byName, byPlate] = await Promise.all([
        fetchDossiers({ ...base, "driver.person.fullName": trimmed }),
        fetchDossiers({ ...base, "vehicle.licensePlate.identifier": trimmed }),
      ]);

      const merged = new Map<string, Dossier>();
      for (const dossier of [...byName.member, ...byPlate.member]) {
        merged.set(dossier.id, dossier);
      }

      return [...merged.values()];
    },
  });

  const confirm = () => {
    if (!selected) return;

    moveMutation.mutate(
      { conversationId, dossierId: selected },
      {
        onSuccess: () => {
          message.success(
            isLinked ? "Gesprek verplaatst" : "Gesprek gekoppeld aan dossier",
          );
          setOpen(false);
          setSelected(undefined);
          setSearch("");
        },
        onError: () => message.error("Koppelen/verplaatsen mislukt"),
      },
    );
  };

  return (
    <>
      <Button
        block={block}
        icon={isLinked ? <SwapOutlined /> : <LinkOutlined />}
        onClick={() => setOpen(true)}
      >
        {isLinked ? "Verplaats naar dossier" : "Koppel aan dossier"}
      </Button>

      <Modal
        title={isLinked ? "Verplaats naar ander dossier" : "Koppel aan dossier"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={confirm}
        okText={isLinked ? "Verplaatsen" : "Koppelen"}
        okButtonProps={{ disabled: !selected, loading: moveMutation.isPending }}
      >
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            Zoek een actief dossier op bestuurdersnaam of kenteken.
          </Text>
          <Select
            style={{ width: "100%" }}
            showSearch
            placeholder="Zoek dossier..."
            filterOption={false}
            onSearch={setSearch}
            onChange={(value) => setSelected(value)}
            value={selected}
            loading={dossiersQuery.isFetching}
            notFoundContent={
              dossiersQuery.isFetching ? "Zoeken..." : "Geen dossiers"
            }
            options={(dossiersQuery.data ?? []).map((dossier) => ({
              value: dossier.id,
              label: dossierLabel(dossier),
            }))}
          />
        </Space>
      </Modal>
    </>
  );
};
